"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Clock, UserCheck, Armchair, Plane, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const features = [{
  id: 1,
  title: "Professional chauffeurs",
  description: "Every driver is suited, thoroughly vetted, and committed to quiet, attentive hospitality.",
  icon: UserCheck
}, {
  id: 2,
  title: "Punctuality guaranteed",
  description: "We monitor your flight in real time and stage curbside ten minutes before scheduled arrival.",
  icon: Clock
}, {
  id: 3,
  title: "Rigorous safety standards",
  description: "Vehicles undergo frequent mechanical diagnostics and follow strict sanitization protocols.",
  icon: ShieldCheck
}, {
  id: 4,
  title: "Executive comfort",
  description: "Relax in hushed acoustic cabins appointed with climate-controlled leather and mobile device charging.",
  icon: Armchair
}];
const assurances = ["Guaranteed flat rates", "Zero surge pricing", "24/7 flight monitoring"];
export default function Principles() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.09
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-choose-us" data-nav="dark" className="dark bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      {/* Subtle ambient lighting motif */}
      <div aria-hidden="true" className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[350px] bg-primary/5 blur-[120px] rounded-lg" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-4 px-3.5 py-1.5 rounded-lg border border-primary/30 bg-primary/10">
              <span className="w-1.5 h-1.5 rounded-lg bg-primary" />
              <span className="text-xs uppercase tracking-wider font-semibold text-primary font-default">
                The Santa Limo Standard
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-default tracking-tight text-foreground leading-tight">
              Travel with <span className="italic font-serif text-primary">absolute</span> certainty.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
              Enjoy guaranteed flat rates, zero surge pricing, and seasoned executive chauffeurs across Illinois and neighboring states.
            </p>
          </div>

          {/* Quick Assurance Badges */}
          <div className="flex flex-wrap gap-2.5 sm:gap-3">
            {assurances.map((item, idx) => <div key={idx} data-index={idx} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border/80 bg-card text-xs font-medium text-muted-foreground">
                <CheckCircle2 className="size-3.5 text-primary stroke-[2]" />
                <span>{item}</span>
              </div>)}
          </div>
        </div>

        {/* Dynamic Split Layout: Core Principles & Terminal Greeting Showcase */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-stretch">
          {/* Left Column: 4-Item Executive Principles Grid */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {features.map((feature, i) => {
            const Icon = feature.icon;
            return <motion.div key={feature.id} data-index={i} variants={itemVariants} className="group relative rounded-xl border border-border bg-card p-6 flex flex-col justify-between transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5">
                  {/* Hairline accent line on top */}
                  <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

                  <div>
                    <div className="w-11 h-11 rounded-lg bg-secondary flex items-center justify-center border border-border/60 mb-5 text-primary group-hover:text-primary transition-colors">
                      <Icon className="size-5 stroke-[1.5]" />
                    </div>
                    <h3 className="text-lg font-semibold font-default text-card-foreground tracking-tight mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed font-default">
                      {feature.description}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-border/40 flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-mono text-muted-foreground/60">0{i + 1}</span>
                    <span className="text-primary/80 font-medium group-hover:text-primary transition-colors">
                      Standard
                    </span>
                  </div>
                </motion.div>;
          })}
          </motion.div>

          {/* Right Column: Terminal Greeting & Branded Fleet Showcase Card */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.15
        }} transition={{
          duration: 0.5,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-6 rounded-xl border border-border bg-card overflow-hidden relative flex flex-col justify-between shadow-2xl transition-[border-color,box-shadow] duration-300 hover:border-primary/40">
            {/* Top gold hairline accent */}
            <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent z-20" />

            {/* Curbside Photography with Visual Gradient Overlay */}
            <div className="relative aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_chauffeur_airport_curbside.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_chauffeur_airport_curbside.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1798015/img/executive_chauffeur_airport_curbside.png-wvc-sizes" alt="Professional chauffeur in black suit holding open rear door of Santa Limo branded luxury sedan at airport terminal" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              {/* Scrim overlay blending naturally with the card base */}
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-card via-card/25 to-transparent" />

              {/* Floating Curbside Status Pill */}
              <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border/80 bg-background/85 backdrop-blur-md text-xs font-mono text-primary font-medium shadow-md">
                <Plane className="size-3.5 text-primary stroke-[1.5]" />
                <span>Curbside & FBO Service</span>
              </div>
            </div>

            {/* Showcase Details & CTAs */}
            <div className="p-6 sm:p-8 flex flex-col justify-between flex-1 gap-6">
              <div>
                <span className="text-xs uppercase tracking-widest font-mono text-primary font-medium">
                  Direct Terminal Greeting
                </span>
                <h3 className="mt-2 text-xl sm:text-2xl font-bold font-default text-foreground leading-snug tracking-tight">
                  Our premier branded luxury sedans and SUVs staged for smooth departure.
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-default">
                  Available around the clock across Chicago O&apos;Hare, Midway, and private aviation terminals throughout Illinois and surrounding regions.
                </p>
              </div>

              <div className="pt-3 border-t border-border/50 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Button asChild variant="default" className="rounded-md font-semibold px-6 py-2.5">
                    <Link to="/OrderPage">Book your ride</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-md px-5 py-2.5">
                    <Link to="/AboutUsPage">Our fleet</Link>
                  </Button>
                </div>
                <span className="text-xs font-mono text-muted-foreground">
                  Flat-Rate • No Meter
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}